'use client'

import React, { useCallback, useEffect, useState } from 'react'

import { X } from 'lucide-react'

import { Spinner } from '@/components/ui/spinner'
import type { Locale } from '@/i18n'
import type { Tag, Vacancy } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { useDebounce } from '@/utilities/useDebounce'

import { VacancyCard } from './VacancyCard'

interface VacancyFilterProps {
  vacancies: Vacancy[]
  tags: Tag[]
  locale: Locale
}

interface FilterButtonProps {
  isSelected: boolean
  onClick: () => void
  children: React.ReactNode
}

function FilterButton({ isSelected, onClick, children }: FilterButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        'touch-manipulation select-none',
        // Prevent shrinking on mobile scroll
        'flex-shrink-0 snap-start',
        isSelected
          ? 'border-transparent bg-primary text-primary-foreground'
          : 'border-border text-foreground hover:bg-primary hover:text-primary-foreground',
      )}
    >
      {children}
    </button>
  )
}

// Helper function to extract plain text from rich text
function extractTextFromRichText(richText: any): string {
  if (!richText || !richText.root || !richText.root.children) return ''

  const extractFromNode = (node: any): string => {
    if (node.type === 'text') {
      return node.text || ''
    }
    if (node.children && Array.isArray(node.children)) {
      return node.children.map(extractFromNode).join(' ')
    }
    return ''
  }

  return richText.root.children.map(extractFromNode).join(' ').trim()
}

export function VacancyFilter({ vacancies, tags, locale }: VacancyFilterProps) {
  const [selectedTags, setSelectedTags] = useState<number[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const debouncedSearchQuery = useDebounce(searchQuery, 700)

  const t = {
    uk: {
      all: 'Всі',
      noResults: 'Немає вакансій за обраними критеріями',
      searchPlaceholder: 'Пошук вакансій...',
    },
    en: {
      all: 'All',
      noResults: 'No vacancies match selected criteria',
      searchPlaceholder: 'Search vacancies...',
    },
  }[locale]

  const toggleTag = useCallback((tagId: number) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    )
  }, [])

  const clearFilters = useCallback(() => {
    setSelectedTags([])
  }, [])

  const clearSearch = useCallback(() => {
    setSearchQuery('')
  }, [])

  // Track loading state
  useEffect(() => {
    if (searchQuery !== debouncedSearchQuery) {
      setIsSearching(true)
    } else {
      // Add minimum loading time of 300ms for better UX
      const timer = setTimeout(() => {
        setIsSearching(false)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [searchQuery, debouncedSearchQuery])

  // Filter vacancies by tags and search (for main list)
  const filteredVacancies = vacancies.filter((vacancy) => {
    // Filter by tags (OR logic - vacancy matches if it has ANY of the selected tags)
    const matchesTags =
      selectedTags.length === 0 ||
      (vacancy.tags &&
        vacancy.tags.length > 0 &&
        vacancy.tags.some((tag) => {
          const tagId = typeof tag === 'number' ? tag : tag.id
          return selectedTags.includes(tagId)
        }))

    // Filter by search query
    const matchesSearch =
      debouncedSearchQuery.trim() === '' ||
      vacancy.title?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      (vacancy.shortDescription &&
        extractTextFromRichText(vacancy.shortDescription)
          .toLowerCase()
          .includes(debouncedSearchQuery.toLowerCase()))

    return matchesTags && matchesSearch
  })

  return (
    <div>
      {/* Search Field */}
      <div className="mb-4 md:mb-6">
        <div className="relative">
          <input
            type="text"
            id="vacancy-search"
            name="vacancy-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.searchPlaceholder}
            className={cn(
              'w-full px-4 py-3 pr-10 rounded-lg',
              'bg-white/5 border-2 border-border',
              'text-foreground placeholder:text-muted-foreground',
              'focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
              'transition-colors',
            )}
          />
          {searchQuery && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isSearching ? (
                <div className="flex items-center justify-center h-5 w-5">
                  <Spinner size="sm" className="text-muted-foreground" />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center h-5 w-5"
                  aria-label="Clear search"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filter Section - horizontal scroll on mobile, wrap on desktop */}
      <div className="relative">
        <div
          className={cn(
            // Base styles
            'flex items-center gap-2 py-3 mb-4 px-4 md:px-0',
            // Mobile: horizontal scroll
            'overflow-x-auto md:overflow-visible',
            'md:flex-wrap md:mb-8',
            // Scroll behavior
            'snap-x snap-mandatory',
            // Hide scrollbar but keep functionality
            '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
            // Sticky positioning
            'sticky top-[64px] z-10',
            'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60',
            // Desktop: remove sticky background
            'md:static md:bg-transparent md:backdrop-blur-none',
          )}
        >
          {/* All/Clear Filter Button */}
          <FilterButton isSelected={selectedTags.length === 0} onClick={clearFilters}>
            {t.all}
          </FilterButton>

          {/* Tag Filter Buttons */}
          {tags.map((tag) => (
            <FilterButton
              key={tag.id}
              isSelected={selectedTags.includes(tag.id)}
              onClick={() => toggleTag(tag.id)}
            >
              {tag.name}
            </FilterButton>
          ))}
        </div>

        {/* Fade gradient indicator - mobile only */}
        <div className="md:hidden absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background via-background/80 to-transparent pointer-events-none" />
      </div>

      {/* Vacancies List */}
      {filteredVacancies.length === 0 ? (
        <p className="text-lg text-gray-400">{t.noResults}</p>
      ) : (
        <div className="space-y-4">
          {filteredVacancies.map((vacancy) => (
            <VacancyCard key={vacancy.id} vacancy={vacancy} locale={locale} />
          ))}
        </div>
      )}
    </div>
  )
}
